<template>
	<div
		:style="$grid('1-9-1')"
		class="ApplicationContainer"
	>
		<div class="actions">
			<InputCheckbox
				:useSquare="true"
				:isControlled="true"
				:modelValue="
					application.selected || (isLenderEntry && application.id == null && fimenu.lender.lenderCode == lenderCode && !props.anyApplicationIsSelected)
				"
				cssClass="button-nothing button-nothing-green"
				@click="() => handleSelect(application)"
				:invalid="invalidInputCheckBox"
				:disabled="disableInputCheckbox"
				class="ignore-deal-lock"
				style="padding-right: 0%"
			/>

			<span style="font-size: 0.75em">{{ applicationType }}</span>
		</div>
		<div class="grid-col">
			<div class="details">
				<div class="value">
					<label>Status:</label>
					<span>
						<i
							v-if="!isLenderEntry"
							:class="getStatusInfo(application)?.icon + ' ' + getStatusInfo(application)?.cssClass"
						></i>
						{{ getStatusInfo(application)?.name }}
					</span>
				</div>
				<div class="value">
					<label>App #:</label>
					<span>
						{{ application.providerAppNumber ?? '-' }}
					</span>
				</div>

				<div class="value">
					<label>Date:</label>
					<span>
						{{ application.decision.decisionDateTime ? util.toMoment(application.decision.decisionDateTime).format('MM/DD/YYYY') : '-' }}
					</span>
				</div>

				<div class="value">
					<label>Time:</label>
					<span>
						{{ application.decision.decisionDateTime ? util.toMoment(application.decision.decisionDateTime).format('hh:mm A') : '-' }}
					</span>
				</div>

				<div class="value">
					<label>VIN:</label>
					<span>
						{{ application.vin ?? '-' }}
					</span>
				</div>
			</div>
			<div class="details">
				<div class="value">
					<label>Amount:</label>
					<span>
						{{ application.decision.decisionNumbers.balanceAmount ? util.formatCurrency(application.decision.decisionNumbers.balanceAmount) : '-' }}
					</span>
				</div>

				<div class="value">
					<label>Trade Amt:</label>
					<span>
						{{ application.decision.decisionNumbers.netTradeAmount ? util.formatCurrency(application.decision.decisionNumbers.netTradeAmount) : '-' }}
					</span>
				</div>

				<div class="value">
					<label>Rate:</label>
					<span>
						{{ getDisplayRateWithSymbol(application) }}
					</span>
				</div>

				<div class="value">
					<label>Term:</label>
					<span> {{ application?.decision?.decisionNumbers?.term ?? '-' }}</span>
				</div>

				<div class="value">
					<label>Tier:</label>
					<span> {{ application?.decision?.tier ?? '-' }}</span>
				</div>
			</div>
		</div>
		<div class="actions">
			<button
				:disabled="application.status == CreditApplicationDecision.Pending"
				@click="() => handleViewApplicationOrLenderInformation(application)"
				class="hovered-desc ignore-all-locks"
				:data-title="getButtonHoverMessage(application)"
			>
				<i
					:class="application?.isSoftPull ? 'fas fa-feather' : 'fas fa-meteor'"
					style="color: white"
				/>

				<i
					v-if="unreadSaturnMessages.length > 0"
					class="notification-icon fas fa-circle"
					style="color: white; font-size: 10px"
				/>
			</button>

			<button
				:disabled="application.id == null || application.status == CreditApplicationDecision.Pending"
				class="hovered-desc ignore-all-locks"
				style="width: 50px"
				data-title="View Messages"
				@click="() => openViewMessagesModal(application)"
			>
				<i
					class="fas fa-envelope"
					style="color: white; font-size: 15px"
				></i>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { CreditApplicationDecision, CreditApplicationDecisionToName } from '@core/classes/CreditApplication';
	import { DEAL_STATUS } from '@core/classes/Enums';
	import { DealType, FinanceChannelsProvider } from '@core/classes/SharedEnums';
	import { computed, inject, ref } from 'vue';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import CreditApplication, { CreditApplicationType } from '@core/classes/CreditApplication';
	import FIDealJacketAdditionalInformation from '@core/classes/FIDealJacketAdditionalInformation';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuCreditApplication from '@core/classes/FIMenuCreditApplication';
	import LenderLease from '@core/classes/LenderLease';
	import util from '@core/services/util';
	import VersionsLender from '@core/classes/LenderVersionClass';
	import { getCreditDecisionStatusInfo, getStatusDescriptionFromCode, getFinanceTypeDescriptionFromCode } from '@core/helpers/finance-channels-helper';

	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import modalRouteOneView from '@/modals/modalRouteOneView.vue';
	import ModalViewFinanceChannelsMessages from '@/modals/modalViewFinanceChannelsMessages.vue';
	import ModalLenderInformation from '@/modals/modalLenderInformation.vue';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import { SaturnMessage } from '@core/classes/SaturnMessage';

	const $global = inject<SaturnGlobalPlugin>('$global');
	const emit = defineEmits(['ApplicationSelected']);

	const props = withDefaults(
		defineProps<{
			application: FIMenuCreditApplication;
			lendersList: VersionsLender[];
			send: (lender: VersionsLender, submissionInformation: FIDealJacketAdditionalInformation) => void;
			reserveProfitCalcs: any;
			fimenu: FIMenu;
			dealHasNoLender: boolean;
			showBalloonIcon: (lenderCode: string) => boolean;
			handleSelectLender: (lender: VersionsLender, isApplication?: boolean, selectedProvider?: FinanceChannelsProvider) => void;
			isLenderEntry?: boolean; // IF its not a creidt app and is a lender entry
			lenderCode?: string | undefined;
			anyApplicationIsSelected: boolean;
			saturnMessages: SaturnMessage[];
		}>(),
		{
			isLenderEntry: false,
			lenderCode: undefined,
		},
	);

	const unreadSaturnMessages = computed(() => props.saturnMessages.filter(m => m.isRead === false));

	const isLoading = ref(false);
	const balloonLenders = props.lendersList.filter(x => x.versions[0].retail.allowBalloonPayment).map(x => x.lenderCode);

	const getButtonHoverMessage = (ca: FIMenuCreditApplication) => {
		if (props.isLenderEntry) return 'View Lender Information';

		return ca?.isSoftPull ? 'Soft Pull' : 'Hard Pull';
	};

	const openViewMessagesModal = (ca: FIMenuCreditApplication) => {
		$modal.open(ModalViewFinanceChannelsMessages, {
			name: 'modalViewFinanceChannelsMessages',
			passedData: { row: ca, fimenu: props.fimenu },
			backdrop: false,
		});
	};

	const handleViewApplicationOrLenderInformation = (ca: FIMenuCreditApplication) => {
		if (props.isLenderEntry) {
			const lender = props.lendersList.find(x => x.lenderCode == props.lenderCode);
			if (!lender) return util.toastr('error', 'View Application/Lender Information', "Couldn't find lender. #695494");

			openLenderInformationModal(lender);
		} else {
			openViewModal(ca);
		}
	};

	const openLenderInformationModal = (lender: VersionsLender) => {
		$modal.open(ModalLenderInformation, {
			name: 'modalLenderInformation',
			passedData: {
				deal: props.fimenu,
				lender: lender,
				reserveProfitCalcs: props.reserveProfitCalcs,
			},
			backdrop: false,
		});
	};

	const openViewModal = (ca: FIMenuCreditApplication) => {
		if (ca.status == null || ca.status == CreditApplicationDecision.Pending) return;

		const lender = props.lendersList.find(x => x.lenderCode == ca.lender.lenderCode);
		$modal.open(modalRouteOneView, {
			name: 'modalRouteOneView',
			passedData: { row: ca, lender, reserveProfitCalcs: props.reserveProfitCalcs, fimenu: props.fimenu },
			backdrop: false,
		});
	};

	const fetchDealFromQuote = async (dealId: string) => {
		try {
			isLoading.value = true;
			const response = await api.fimenu.getDeal(dealId);

			if (response.data) {
				props.fimenu.copyFromQuote(response.data.fimenu);
			}
		} finally {
			isLoading.value = false;
		}
	};

	const isMFRateForApplication = (app: CreditApplication) => {
		switch (app.type) {
			case CreditApplicationType.Lease: {
				const applicationLender = props.lendersList.find(x => x.lenderCode == app?.lender?.lenderCode);

				if (applicationLender) {
					var lenderForTheApplication = applicationLender.getLenderByDealType(DealType.Lease) as LenderLease;
					if (lenderForTheApplication && lenderForTheApplication.isMoneyFactorAsRate) {
						return true;
					}
				}
				return false;
			}
			case CreditApplicationType.Retail:
				return false;
			default:
				return false;
		}
	};

	const getFormattedRateFromDecision = (app: CreditApplication) => {
		var rate = getRateFromDecision(app);

		if (rate == null) return '-';
		else return `${rate}`;
	};

	const getRateFromDecision = (app: CreditApplication) => {
		if (app.type == CreditApplicationType.Lease) {
			if (isMFRateForApplication(app)) return app.decision.decisionNumbers.annualPercentageRate;
			else return app.decision.decisionNumbers.leaseRateMoneyFactor;
		} else {
			return app.decision.decisionNumbers.annualPercentageRate;
		}
	};

	const getStatusInfo = (application: FIMenuCreditApplication) => {
		return getCreditDecisionStatusInfo(application?.status);
	};

	const handleSelect = (application?: FIMenuCreditApplication) => {
		// Emit application selected
		// Emit that the application has been updated
		emit('ApplicationSelected', application);
		if (props.isLenderEntry) {
			const lender = props.lendersList.find(x => x.lenderCode == props.lenderCode);

			props.handleSelectLender(lender);
		} else {
			handleSelectApplication(application);
		}
	};

	/**
	 * Applies a credit application to the deal
	 */
	const handleSelectApplication = async (selectedData: Partial<FIMenuCreditApplication>) => {
		props.fimenu.dealJacket.applications.forEach(c => {
			c.selected = c.id === selectedData.id;
		});

		const selectedLender = props.lendersList.find(x => x.lenderCode == selectedData.lender.lenderCode);

		props.handleSelectLender(selectedLender, true, selectedData.selectedProvider ?? FinanceChannelsProvider.RouteOne);

		props.fimenu.dealJacket.purchaseVehicleBookValue = selectedData.vehicleBookValue;
		if (!balloonLenders.includes(selectedData.lender.lenderCode)) props.fimenu.loanTerms.isBalloon = false;

		if (selectedData.submissionInformation?.relationship != null) {
			props.fimenu.customersInfo.relationship = selectedData.submissionInformation.relationship;
			props.fimenu.customersInfo.relationshipDescription = selectedData.submissionInformation.relationshipDescription;
		}

		//// This will preload the current deal with the quote deal information.
		//await this.fetchDealFromQuote(selectedData.dealId);
	};

	/**
	 * Determines if the checkbox should be disabled.
	 * Conditions:
	 * - If application has no lender code and it's not a lender entry
	 * - If application type doesn't match the current deal type
	 */
	const disableInputCheckbox = computed(() => {
		return (
			(props.application.lender.lenderCode == null && !props.isLenderEntry) ||
			(!props.application.MatchesDealType(props.fimenu.dealType) && props.application.type != null) ||
			props.fimenu.isDealFinalized()
		);
	});

	/**
	 * Determines if the checkbox should show as invalid/warning state.
	 * Returns true when the application matches the deal type, has a valid lender code,
	 * but the deal currently has no lender selected.
	 */
	const invalidInputCheckBox = computed(() => {
		return props.application.MatchesDealType(props.fimenu.dealType) && props.application.lender.lenderCode != null && props.dealHasNoLender;
	});

	const applicationType = computed(() => {
		switch (props.application.type) {
			case CreditApplicationType.Lease:
				return 'Lease';
			case CreditApplicationType.Retail:
				return 'Retail';
			default:
				return '';
		}
	});

	/**
	 * Formats the rate for display and determines if the '%' symbol should be appended
	 * @param app The credit application
	 * @returns Formatted rate with appropriate symbol
	 */
	const getDisplayRateWithSymbol = (app: CreditApplication) => {
		// Get the base rate value formatted as string
		const formattedRate = getFormattedRateFromDecision(app);

		// Determine if we should append a '%' symbol
		// Append '%' if:
		// 1. It's a retail application OR
		// 2. It's a money factor displayed as rate AND there's a valid rate value
		const shouldAppendPercentSign = app.type === CreditApplicationType.Retail || (isMFRateForApplication(app) && formattedRate !== '-');

		// Return the formatted rate with or without the '%' symbol
		return formattedRate + (shouldAppendPercentSign ? '%' : '');
	};
</script>

<style>
	.ApplicationContainer {
		display: flex;
		box-sizing: border-box;
		align-items: center;
		justify-content: space-between;
		padding: 10px;
		border-bottom: 1px solid var(--main-color);
		margin-bottom: 10px;
	}

	.ApplicationContainer .actions {
		display: flex;
		flex-direction: column;
		justify-content: space-evenly;
		align-items: center;
		height: 100%;
		gap: 10px;
	}
	.ApplicationContainer .actions button {
		padding: 0 10px;
		width: unset !important;
		position: relative;
	}

	.ApplicationContainer .grid-col {
		display: grid;
		grid-template-columns: 1fr 1fr;
		font-size: 1em;
		width: 100%;
		gap: 15px;
	}
	.ApplicationContainer .grid-col .value {
		display: grid;
		grid-template-columns: 11fr 19fr;
		align-items: center;
		justify-content: space-between;
	}
	.ApplicationContainer .grid-col .value:nth-child(2n) {
		background-color: var(--richtable-alternating-background-color);
	}
	.ApplicationContainer .grid-col .value label {
		font-weight: 600;
		font-size: 0.85em;
		place-self: start;
		text-wrap: nowrap;
		overflow: hidden;
		width: 100%;
		text-overflow: ellipsis;
	}
	.ApplicationContainer .grid-col .value span {
		font-size: 0.75em;
		place-self: center;
	}

	/* Define icon colors */
	.icon-error {
		color: #dc3545; /* Red for error */
	}

	.icon-approved {
		color: #28a745; /* Green for approved */
	}

	.icon-denied {
		color: #dc3545; /* Red for denied */
	}

	.icon-conditional {
		color: #ffc107; /* Yellow for conditional */
	}

	.icon-preliminary {
		color: #17a2b8; /* Blue for preliminary */
	}

	.icon-withdrawn {
		color: #6c757d; /* Gray for withdrawn */
	}

	.icon-new {
		color: #007bff; /* Blue for new */
	}

	.icon-waiting {
		color: #dc3545; /* Red for waiting */
	}

	.notification-icon {
		position: absolute;
		top: -3px;
		right: -3px;
		color: var(--error-color) !important;
	}
</style>
